import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TeamDisplay from "@/components/TeamDisplay.jsx";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

export const RhinoshieldStepWelcome = ({ onStart, contestData }) => {
    const { t } = useTranslation();
    const { brand, welcome } = useBrandConfig();

    const [isVisible, setIsVisible] = useState(false);
    const [splashState, setSplashState] = useState('animating');

    const homeTeam = contestData?.fixture?.homeTeam || { name: "France", shortName: "FRA", logoUrl: "https://flagcdn.com/w160/fr.png" };
    const awayTeam = contestData?.fixture?.awayTeam || { name: "Argentine", shortName: "ARG", logoUrl: "https://flagcdn.com/w160/ar.png" };

    useEffect(() => {
        const exitTimer = setTimeout(() => setSplashState('exiting'), 1200);
        const doneTimer = setTimeout(() => { setSplashState('done'); setIsVisible(true); }, 1700);
        return () => { clearTimeout(exitTimer); clearTimeout(doneTimer); };
    }, []);

    const logoProps = {
        flagW: "w-14 min-[380px]:w-16",
        flagH: "h-10 min-[380px]:h-12",
        textColor: "font-black text-sm min-[380px]:text-base uppercase tracking-tighter",
    };

    const customStyles = `
        /* --- ANIMATIONS E-COMMERCE PREMIUM --- */
        @keyframes rhino-float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-12px); }
        }
        .animate-rhino-float { animation: rhino-float 4s ease-in-out infinite; }

        @keyframes rhino-boom {
            0% { transform: scale(0.8); opacity: 0; filter: blur(10px); }
            50% { transform: scale(1.05); opacity: 1; filter: blur(0px); }
            100% { transform: scale(1); opacity: 1; }
        }
        .animate-rhino-boom { animation: rhino-boom 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; }

        @keyframes rhino-shockwave {
            0% { transform: scale(1); opacity: 0.15; }
            100% { transform: scale(3.5); opacity: 0; }
        }
        .animate-rhino-shockwave { animation: rhino-shockwave 1.2s cubic-bezier(0, 0, 0.2, 1) infinite; }

        .stagger-1 { transition-delay: 100ms; }
        .stagger-2 { transition-delay: 200ms; }
        .stagger-3 { transition-delay: 300ms; }
        .stagger-4 { transition-delay: 400ms; }

        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        /* --- GRILLE TECHNIQUE (Inspirée des plans industriels) --- */
        .bg-tech-grid {
            background-color: ${brand.colors.pageBg};
            background-size: 32px 32px;
            background-image:
                linear-gradient(to right, ${brand.colors.highlight}0A 1px, transparent 1px),
                linear-gradient(to bottom, ${brand.colors.highlight}0A 1px, transparent 1px);
        }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col bg-tech-grid">
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* --- SPLASH SCREEN --- */}
            {splashState !== 'done' && (
                <div
                    className={`absolute inset-0 z-[100] flex flex-col items-center justify-center bg-white transition-all duration-500 ease-out
                        ${splashState === 'exiting' ? 'scale-110 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}`}
                >
                    <div className="absolute w-32 h-32 rounded-full animate-rhino-shockwave" style={{ backgroundColor: brand.colors.highlight }} />
                    <BrandLogo className="w-64 object-contain animate-rhino-boom drop-shadow-xl relative z-10" />
                </div>
            )}

            {/* --- HEADER --- */}
            <div className="w-full pt-14 pb-4 flex justify-center items-center z-20 relative shrink-0">
                <BrandLogo className="h-6 object-contain" />
            </div>

            {/* --- CONTENU --- */}
            <div className="flex-1 overflow-y-auto pb-48 hide-scrollbar relative px-6">
                <div className="flex flex-col items-center justify-center min-h-full max-w-sm mx-auto py-2 gap-8">

                    {/* TITRE PRINCIPAL */}
                    <div className={`text-center transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} stagger-1`}>
                        <h1
                            className="font-heading text-[3.2rem] min-[380px]:text-[3.8rem] leading-[0.9] uppercase tracking-tighter"
                            style={{ color: brand.colors.highlight }}
                        >
                            {t('holy.welcome.title_line1')} <br/>
                            <span
                                className="relative inline-block px-2 mt-1"
                                style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                            >
                                {t('holy.welcome.title_line2')}
                            </span>
                        </h1>
                    </div>

                    {/* AFFICHE DU MATCH */}
                    <div className={`w-full transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} stagger-2`}>
                        <div
                            className="flex items-center w-full px-2 py-4 rounded-2xl border bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                            style={{ borderColor: `${brand.colors.highlight}1A` }}
                        >
                            <div className="flex-1 flex justify-center" style={{ color: brand.colors.highlight }}>
                                <TeamDisplay {...logoProps} team={homeTeam} />
                            </div>
                            <div className="flex flex-col items-center shrink-0 px-2">
                                <div
                                    className="px-3 py-1 rounded-md text-sm font-heading font-bold"
                                    style={{ backgroundColor: brand.colors.highlight, color: brand.colors.pageBg }}
                                >
                                    VS
                                </div>
                            </div>
                            <div className="flex-1 flex justify-center" style={{ color: brand.colors.highlight }}>
                                <TeamDisplay {...logoProps} team={awayTeam} />
                            </div>
                        </div>
                    </div>

                    {/* LOT À GAGNER */}
                    <div className={`relative w-full transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} stagger-3`}>
                        <div
                            className="relative flex flex-col items-center justify-center rounded-2xl border bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 pt-10"
                            style={{ borderColor: `${brand.colors.highlight}1A` }}
                        >
                            {/* Badge */}
                            <div
                                className="absolute top-4 left-4 z-20 font-black text-[10px] px-3 py-1 rounded uppercase tracking-[0.2em]"
                                style={{ backgroundColor: brand.colors.highlight, color: brand.colors.pageBg }}
                            >
                                {t('holy.welcome.reward_badge')}
                            </div>

                            {/* Scène Produit */}
                            {welcome.rewardImageUrl && (
                                <div className="w-full flex flex-col items-center justify-center">
                                    <img
                                        src={welcome.rewardImageUrl}
                                        alt={welcome.rewardTitle}
                                        className="h-44 min-[380px]:h-48 w-auto object-contain animate-rhino-float"
                                    />
                                </div>
                            )}

                            {/* Textes Produit */}
                            <div className="flex flex-col items-center text-center mt-6">
                                <h2
                                    className="font-heading text-4xl uppercase leading-none tracking-tighter"
                                    style={{ color: brand.colors.highlight }}
                                >
                                    {welcome.rewardTitle}
                                </h2>
                                <span
                                    className="font-bold uppercase tracking-widest mt-2 text-[11px]"
                                    style={{ color: brand.colors.highlight, opacity: 0.5 }}
                                >
                                    {welcome.rewardSubtitle}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* --- FOOTER CTA --- */}
            <div
                className={`absolute bottom-0 left-0 w-full px-6 pb-8 pt-24 z-30 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} stagger-4`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg} 70%, transparent)` }}
            >
                <div className="pointer-events-auto max-w-sm mx-auto flex flex-col items-center">
                    <button
                        id="demo-btn-start"
                        onClick={onStart}
                        className="group w-full flex items-center justify-center gap-3 py-5 rounded-full font-heading text-2xl uppercase italic tracking-wider shadow-lg active:translate-y-1 active:shadow-none transition-all"
                        style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                    >
                        {t('holy.welcome.cta')}
                        <ChevronRight size={28} strokeWidth={3} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <footer className="flex items-center justify-center gap-4 mt-5">
                        <button className="text-[10px] font-bold uppercase tracking-[0.1em] transition-opacity hover:opacity-100" style={{ color: brand.colors.highlight, opacity: 0.4 }}>{t('footer.rules')}</button>
                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: brand.colors.highlight, opacity: 0.2 }} aria-hidden="true" />
                        <button className="text-[10px] font-bold uppercase tracking-[0.1em] transition-opacity hover:opacity-100" style={{ color: brand.colors.highlight, opacity: 0.4 }}>{t('footer.cgps')}</button>
                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: brand.colors.highlight, opacity: 0.2 }} aria-hidden="true" />
                        <button className="text-[10px] font-bold uppercase tracking-[0.1em] transition-opacity hover:opacity-100" style={{ color: brand.colors.highlight, opacity: 0.4 }}>{t('footer.legal')}</button>
                    </footer>
                </div>
            </div>
        </div>
    );
};
